import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserQueries.css';
import { API, UPLOADS_URL, authHeadersOnly } from '../services/apiConfig';

const STATUS_COLORS = {
  OPEN:        { label: 'Open',         color: '#d97706', bg: '#fef3c7' },
  IN_PROGRESS: { label: 'In Progress',   color: '#1d4ed8', bg: '#dbeafe' },
  RESOLVED:    { label: 'Resolved',      color: '#16a34a', bg: '#dcfce7' },
  CLOSED:      { label: 'Closed',        color: '#6b7280', bg: '#f3f4f6' },
};

const QUERY_TABS = ['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

export default function UserQueries({ user, onClose, embedded }) {
  const [allQueries, setAllQueries] = useState([]);
  const [shown, setShown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('ALL');
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const PER = 5;

  useEffect(() => { load(); }, []);
  useEffect(() => { filter(allQueries, tab, search, 0); setPage(0); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      console.log('Fetching queries for userId:', user.userId);
      // Try the user-specific endpoint first
      try {
        const { data } = await axios.get(`${API}/api/contact/user/${user.userId}`, { headers: authHeadersOnly() });
        console.log('Queries loaded from user endpoint:', data);
        const sorted = (data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllQueries(sorted);
        filter(sorted, tab, search, 0);
      } catch (userEndpointError) {
        console.log('User endpoint failed, trying admin endpoint:', userEndpointError.message);
        // Fallback: Get all queries and filter by user email
        const { data } = await axios.get(`${API}/api/contact`, { headers: authHeadersOnly() });
        const userQueries = (data || []).filter(q => q.email === user.email);
        console.log('Queries loaded from admin endpoint (filtered):', userQueries);
        const sorted = userQueries.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllQueries(sorted);
        filter(sorted, tab, search, 0);
      }
    } catch (err) { 
      console.error('Error fetching queries:', err);
      setAllQueries([]); 
      setShown([]); 
    }
    finally { setLoading(false); }
  };

  const filter = (data, t, q, p) => {
    let f = t === 'ALL' ? data : data.filter(query => query.status === t);
    if (q.trim()) {
      f = f.filter(query =>
        query.id?.toString().includes(q.trim()) ||
        query.queryType?.toLowerCase().includes(q.toLowerCase()) ||
        query.description?.toLowerCase().includes(q.toLowerCase())
      );
    }
    setPages(Math.ceil(f.length / PER));
    setShown(f.slice(p * PER, p * PER + PER));
  };

  const goPage = (p) => { setPage(p); filter(allQueries, tab, search, p); };

  const deleteQuery = async (e, queryId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this query from your history?')) return;
    setDeleting(queryId);
    try {
      await axios.delete(`${API}/api/contact/${queryId}`, { headers: authHeadersOnly() });
      const updated = allQueries.filter(q => q.id !== queryId);
      setAllQueries(updated);
      filter(updated, tab, search, page);
    } catch {
      alert('Failed to delete query.');
    } finally {
      setDeleting(null);
    }
  };

  const counts = QUERY_TABS.reduce((a, t) => {
    a[t] = t === 'ALL' ? allQueries.length : allQueries.filter(q => q.status === t).length;
    return a;
  }, {});

  if (loading) {
    return (
      <div className={embedded ? 'uq-wrap-emb' : 'uq-wrap'}>
        {!embedded && <div className="uq-backdrop" onClick={onClose} />}
        <div className={embedded ? 'uq-panel-emb' : 'uq-panel'}>
          <div className="uq-loading">
            <div className="uq-spin"></div>
            <p>Loading queries...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'uq-wrap-emb' : 'uq-wrap'}>
      {!embedded && <div className="uq-backdrop" onClick={onClose} />}
      <div className={embedded ? 'uq-panel-emb' : 'uq-panel'}>
        {/* Header */}
        <div className="uq-header">
          <div className="uq-header-left">
            <h2>📬 My Queries</h2>
            <p>Track and manage your support queries</p>
          </div>
          {!embedded && <button className="uq-close" onClick={onClose}>✕</button>}
        </div>

        {/* Search & Tabs */}
        <div className="uq-controls">
          <input
            type="text"
            placeholder="Search by Query ID, Type, or Description..."
            value={search}
            onChange={e => { setSearch(e.target.value); filter(allQueries, tab, e.target.value, 0); setPage(0); }}
            className="uq-search"
          />
          <div className="uq-tabs">
            {QUERY_TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`uq-tab ${tab === t ? 'active' : ''}`}
                style={{
                  background: tab === t ? (STATUS_COLORS[t]?.color || '#666') : 'transparent',
                  color: tab === t ? 'white' : '#666',
                }}
              >
                {t.replace(/_/g, ' ')} ({counts[t]})
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="uq-content">
          {shown.length === 0 ? (
            <div className="uq-empty">
              <div className="uq-empty-icon">📭</div>
              <p>No queries found</p>
            </div>
          ) : (
            <div className="uq-list">
              {shown.map((query) => {
                const sc = STATUS_COLORS[query.status] || STATUS_COLORS.OPEN;
                return (
                  <div key={query.id} className="uq-card">
                    <div className="uq-card-header">
                      <div>
                        <h4 className="uq-query-type">{query.queryType}</h4>
                        <p className="uq-query-id">Query ID: #{query.id}</p>
                      </div>
                      <span className="uq-status" style={{ background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </div>
                    <div className="uq-card-body">
                      <p className="uq-description">{query.description}</p>
                      {query.invoiceNo && <p className="uq-invoice"><strong>Invoice:</strong> {query.invoiceNo}</p>}
                      {query.imageUrl && (
                        <div className="uq-images">
                          {query.imageUrl.split(',').map((url, i) => (
                            <a key={i} href={`${UPLOADS_URL}${url.trim()}`} target="_blank" rel="noreferrer">
                              <img src={`${UPLOADS_URL}${url.trim()}`} alt={`attachment-${i + 1}`} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="uq-card-footer">
                      <span className="uq-date">{fmtDate(query.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="uq-pagination">
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                onClick={() => goPage(i)}
                className={`uq-page-btn ${page === i ? 'active' : ''}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
